import React, { useEffect, useState, useCallback } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { useLanguage } from "../../../../context/LanguageContext";
import { ScreenConfig } from "../Sequences";
import { colors, chartColors, commonStyles } from "../../../../components/colors";

export type BandwagonChoice = 'streaming' | 'cinema';

export interface SelectionData {
    choice: BandwagonChoice;
    millisecondsSinceFirstRender: number;
    defaultOption: BandwagonChoice;
}

export interface BandwagonScreenResult {
    selections: SelectionData[];
    reason?: string;
}

export const BandwagonScreenInitialResult: BandwagonScreenResult = {
    selections: [],
    reason: ""
};

const choiceColors = chartColors.primary; // [lichtBlau, darkGreen]

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 470,
    maxWidth: 600,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 250
};

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 32,
    textAlign: "center",
    fontSize: 17,
    background: colors.backgroundLight,
    maxWidth: 650,
    marginLeft: "auto",
    marginRight: "auto",
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const chartStyle: React.CSSProperties = {
    minWidth: 180,
    maxWidth: 300,
    flex: 1
};

const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

export function BandwagonResultScreen(props: any) {
    const { getGroupData, userData } = useSession();
    const { t, language } = useLanguage();
    const [dataWithInfluence, setDataWithInfluence] = useState<any[]>([]);
    const [dataNeutral, setDataNeutral] = useState<any[]>([]);
    const [totals, setTotals] = useState({ withInfluence: 0, neutral: 0 });
    const [loading, setLoading] = useState(true);

    const [userReason, setUserReason] = useState<string | undefined>("");

    const getChoiceText = useCallback((choice?: BandwagonChoice) => {
        switch (choice) {
            case 'streaming': return t('experiments', 'bandwagon', 'screens', 'task', 'options', 'streaming');
            case 'cinema': return t('experiments', 'bandwagon', 'screens', 'task', 'options', 'cinema');
            default: return t('experiments', 'bandwagon', 'screens', 'results', 'noChoice');
        }
    }, [t]);

    // Only useEffect for *userReason* loading (side effect -- state). DON'T assign or update userChoice/userInGroup here!
    useEffect(() => {
        if (userData && userData.BandwagonEffect) {
            const obj = userData.BandwagonEffect;
            if (obj["0"]?.selections?.length > 0 && typeof obj["0"].reason === "string") {
                const val = obj["0"].reason.trim();
                setUserReason(val.length > 0 ? val : undefined);
            } else {
                setUserReason(undefined);
            }
        } else {
            setUserReason(undefined);
        }
    }, [userData]);

    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            let streamingWithInfluence = 0;
            let cinemaWithInfluence = 0;
            let streamingNeutral = 0;
            let cinemaNeutral = 0;

            data?.forEach((dat: any) => {
                const obj = dat.BandwagonEffect;
                if (obj && obj["0"]?.selections?.length > 0) {
                    const choice = obj["0"].selections[obj["0"].selections.length - 1].choice;
                    const group = dat.randomizer3 || "Bedingung 1";

                    if (group === "Bedingung 1") { // With social influence
                        if (choice === 'streaming') streamingWithInfluence++;
                        if (choice === 'cinema') cinemaWithInfluence++;
                    } else { // Without social influence
                        if (choice === 'streaming') streamingNeutral++;
                        if (choice === 'cinema') cinemaNeutral++;
                    }
                }
            });

            const totalWithInfluence = streamingWithInfluence + cinemaWithInfluence;
            const totalNeutral = streamingNeutral + cinemaNeutral;

            setTotals({
                withInfluence: totalWithInfluence,
                neutral: totalNeutral
            });

            setDataWithInfluence([
                { name: getChoiceText('streaming'), Anzahl: streamingWithInfluence || 0, color: choiceColors[0] },
                { name: getChoiceText('cinema'), Anzahl: cinemaWithInfluence || 0, color: choiceColors[1] }
            ]);

            setDataNeutral([
                { name: getChoiceText('streaming'), Anzahl: streamingNeutral || 0, color: choiceColors[0] },
                { name: getChoiceText('cinema'), Anzahl: cinemaNeutral || 0, color: choiceColors[1] }
            ]);

            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, t, getChoiceText]);

    useEffect(() => {
        props.setCanContinue(true);
    }, [props]);

    // Calculate userChoice and userInGroup ON EVERY RENDER (so they always reflect latest userData)
    let userChoice: BandwagonChoice | undefined = undefined;
    let userInGroup: "Bedingung 1" | "Bedingung 2" = "Bedingung 1";
    if (userData && userData.BandwagonEffect) {
        const obj = userData.BandwagonEffect;
        if (obj["0"]?.selections?.length > 0) {
            userChoice = obj["0"].selections[obj["0"].selections.length - 1].choice;
        }
        userInGroup = userData.randomizer3 || "Bedingung 1";
    }

    const groupNumber = userInGroup === "Bedingung 1" ? "1" : "2";
    const groupType = userInGroup === "Bedingung 1"
        ? t('experiments', 'bandwagon', 'screens', 'results', 'withInfluenceShort')
        : t('experiments', 'bandwagon', 'screens', 'results', 'withoutInfluenceShort');

    const resultMessageRaw = t('experiments', 'bandwagon', 'screens', 'results', 'yourResult')
        .replace('{group}', groupNumber)
        .replace('{groupType}', groupType)
        .replace('{choice}', getChoiceText(userChoice));

    const reasonLabel = language === "de" ? "Deine Begründung:" : "Your explanation:";

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {t('experiments', 'bandwagon', 'screens', 'results', 'loading')}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'bandwagon', 'screens', 'results', 'title')}
            </h2>

            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 28
            }}>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {t('experiments', 'bandwagon', 'screens', 'results', 'withInfluence', 'title')}
                    </h3>
                    <p style={{
                        textAlign: "center",
                        fontSize: 16,
                        color: colors.textMedium,
                        marginBottom: 20
                    }}>
                        {t('experiments', 'bandwagon', 'screens', 'results', 'withInfluence', 'description')}
                    </p>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            <div style={{
                                fontWeight: 500,
                                marginBottom: 12,
                                textAlign: "center",
                                fontSize: 16,
                                color: colors.brillantBlue
                            }}>
                                {t('experiments', 'bandwagon', 'screens', 'results', 'decisions')}
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={dataWithInfluence}>
                                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totals.withInfluence} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {dataWithInfluence.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 12
                    }}>
                        {t('experiments', 'bandwagon', 'screens', 'results', 'withoutInfluence', 'title')}
                    </h3>
                    <p style={{
                        textAlign: "center",
                        fontSize: 16,
                        color: colors.textMedium,
                        marginBottom: 20
                    }}>
                        {t('experiments', 'bandwagon', 'screens', 'results', 'withoutInfluence', 'description')}
                    </p>
                    <div style={chartBlockStyle}>
                        <div style={{ ...chartStyle, margin: "0 auto" }}>
                            <div style={{
                                fontWeight: 500,
                                marginBottom: 12,
                                textAlign: "center",
                                fontSize: 16,
                                color: colors.brillantBlue
                            }}>
                                {t('experiments', 'bandwagon', 'screens', 'results', 'decisions')}
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={dataNeutral}>
                                    <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={totals.neutral} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.2)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={50}>
                                        {dataNeutral.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>

            <div style={resultMessageStyle}>
                <div dangerouslySetInnerHTML={{ __html: resultMessageRaw }} />
                {userReason && userReason.trim().length > 0 && (
                    <div style={{
                        marginTop: 10,
                        fontSize: 15,
                        color: colors.textDark,
                        background: "#f8fafc",
                        borderRadius: 8,
                        padding: "10px 16px",
                        border: `1px solid ${colors.silver}`,
                    }}>
                        <div style={{
                            fontWeight: 600,
                            color: colors.brillantBlue,
                            marginBottom: 4
                        }}>
                            {reasonLabel}
                        </div>
                        <div style={{
                            fontStyle: "italic",
                            color: colors.textMedium
                        }}>
                            {userReason}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export const BandwagonResultScreenConfig: ScreenConfig = {
    component: BandwagonResultScreen,
    defaultResult: {},
    canContinueDefault: true,
};